import { __assign } from "tslib";
import * as React from 'react';
import TextField from '@material-ui/core/TextField';
import { useField } from 'formik';
import { BLayout } from '../BLayout';
import { autoformat, isFormatted, makeLabel } from '../../common/functions';
import { BError } from './BError';
/**
* **Must be a child of BForm**
*
* The `BTextField` is to be used for general TextFields.
* If you want an email or password field use BPassword and BEmail respectively
*
*  **Required Props:**
*  - name
*   - Name and ID of the component. It should match a key in initialValues
*
* **Recommended Props:**
* - label
*  - Label for the TextField.
* - placeholder
*  - The short hint displayed in the input before the user enters a value (appears on focus and disappears on input).
*
* **Optional Props:**
* - autoFormat
*  - If true, ensures that user enters only correct input as defined by the format prop.
*    When format is present, the value is defaulted to true. This behavior can be overridden.
* - format
*  - A format string that will be used for formatting the user input where 9 is numbers and X is characters
* - multiline
*  - Allows the text box to extend to multiple lines after the text fills all the space in one row.
* - required
*  - Default is false. If true the field will be required, and the form can't be submitted without input to this field.
* - rows
*  - Number of rows to display by default when multiline option is set to true
* - [textFieldProps](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/development/docs/types/BTextFieldProps.md)
*  - Almost any [Material-UI TextField props](https://material-ui.com/api/text-field/#props)
* - [BLayoutProps](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/development/docs/types/BLayoutProps.md)
*  - Custom gridding, sizes, and alignment
*  - Default w is 2
*
* **Basic Example:**
* ```jsx
* <BTextField
*   name="firstname"
*   label="First Name"
*   placeholder="Enter first name"
*   textFieldProps={{
*      variant: 'outlined',
*      margin: 'dense'
*    }}
* />;
* ```
* **Format Example:**
* ```jsx
* <BTextField
*   name="phone"
*   format='999-999-9999'
* />;
* ```
*
* Demos:
*
* - [Sample Form](http://mui-bueno.pipeline.east1.simoncomputing.com.s3-website-us-east-1.amazonaws.com/BTextField)
*
* API:
*
* - [BTextField API](https://gitlab.com/2020-bootcamp/forms/mui-bueno/-/blob/development/docs/tag-references/input/BTextField.md)
*/
export var BTextField = function (props) {
    var name = props.name, _a = props.label, label = _a === void 0 ? makeLabel(name) : _a, _b = props.format, format = _b === void 0 ? '' : _b, _c = props.autoFormat, autoFormat = _c === void 0 ? format ? true : false : _c, _d = props.w, w = _d === void 0 ? 2 : _d, _e = props.required, required = _e === void 0 ? false : _e, _f = props.placeholder, placeholder = _f === void 0 ? '' : _f, _g = props.multiline, multiline = _g === void 0 ? false : _g, rows = props.rows, className = props.className;
    // Formik boilerplate ------------------
    var _h = useField({ name: name, validate: function () { return validation(); } }), field = _h[0], meta = _h[1], helpers = _h[2];
    var setValue = helpers.setValue;
    var value = field.value;
    var error = meta.error, touched = meta.touched;
    // -------------------------------------
    if (value === undefined) {
        console.error("Please set an initial value for " + name);
    }
    if (!format && autoFormat) {
        console.error("Cam't auto format when there is no format");
    }
    var handleChange = function (event) {
        //autoFormat ensures that the user enters the correct formatted value
        if (autoFormat) {
            var formattedValue = autoformat(event.target.value, format, value, event.target);
            //Sends formatted value to formik context store
            setValue(formattedValue);
        }
        else {
            //None formatted value is sent to formik context store
            setValue(event.target.value);
        }
    };
    //Validation for required, and formatted 
    var validation = function () {
        if (required && !value) {
            return (props.label ? props.label : 'Field') + " is required";
        }
        if (!format && autoFormat) {
            return "Can't auto format when there is no format specified";
        }
        //If value isnt empty run isFormatted
        if (!value || isFormatted(value, format)) {
            return '';
        }
        else {
            return "Invalid format: " + format;
        }
    };
    return (React.createElement(BLayout, __assign({}, props, { w: w }),
        React.createElement("div", { className: className },
            React.createElement(TextField, __assign({ margin: 'dense', variant: 'outlined', fullWidth: true }, props.textFieldProps, field, { label: label, id: name + "-tf", error: Boolean(error) && touched, onChange: handleChange, placeholder: placeholder, multiline: multiline, rows: rows })),
            React.createElement(BError, { name: name, id: name + "-err" }))));
};
//# sourceMappingURL=BTextField.js.map